import styles from "./Header.module.scss";
import CustomLink from "@Components/CustomLink";
import FullPageLoader from "@Components/FullPageLoader";
import LazyImage from "@Components/Image";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import config from "@Config/index";
import Chevron from "@Icons/Chevron";
import HamburgurIcon from "@Icons/HamburgurIcon";
import LoaderIcon from "@Icons/LoaderIcon";
import SearchIcon from "@Icons/SearchIcon";
import BlueprintLogoSmall from "@Icons/BlueprintLogoSmall";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { PAGE } from "constants/configConstants";
import {
  COMPONENTS_CATEGORY,
  EVENT_LABEL,
  AMBASSADOR_PAGE_STATIC_URL,
  SCHOLARSHIP_PAGE_STATIC_URL,
} from "constants/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import { CSSProperties, Suspense, lazy, useEffect, useState, useRef } from "react";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import formatValidOuterLink from "helpers/formatValidOuterLink";
import getImagePath from "@Utils/getImagePath";
import { useHistory } from "hooks/useHistory";
import useIsApp from "hooks/useIsApp";
import { webAppBackHandler } from "@Utils/webApputils";
import useAppPadding from "hooks/useAppPadding";
import AppSafeAreaSpacer from "@Components/AppSafeAreaSpacer";
import useIsTabletPortrait from "hooks/useIsTabletPortrait";
import getCookie from "helpers/getCookie";
import useCustomWindowLocation from "@Components/CustomWindowLocation";

const HERO_SECTION_ID = "section-featured";
const IPAD_SCROLL_REVEAL_PX = 500;

const DropDownMenu = lazy(() => import("./DropDownMenu"));
const SearchModalMobile = lazy(() => import("@Components/SearchModalMobile"));

interface IProps {
  addShadow?: boolean;
  isProfileHeader?: boolean;
  isSubProfileHeader?: boolean;
  profileHeaderTitle?: string;
  dataTestId?: string;
  hideSearchBar?: boolean;
  isAmberPlusHeader?: boolean;
  stopAmberPlusLogoRedirect?: boolean;
  showBackButton?: boolean;
  showHamburger?: boolean;
  showLangSelector?: boolean;
  addAppPadding?: boolean;
  appSafeAreaStyles?: CSSProperties;
  isReferralPage?: boolean;
  isBlogHeader?: boolean;
  isExamsHeader?: boolean;
  isAmbassadorHeader?: boolean;
  isScholarshipPage?: boolean;
  isCareerPage?: boolean;
  isAboutPage?: boolean;
  isNewsHeader?: boolean;
  isMediaMentionsHeader?: boolean;
  isPodcastHeader?: boolean;
  containerClassName?: string;
  fromSearchPage?: boolean;
  showHeaderAboveMd?: boolean;
}
const Header = (props: IProps) => {
  const {
    addShadow,
    isProfileHeader,
    isSubProfileHeader,
    profileHeaderTitle,
    dataTestId,
    hideSearchBar,
    isAmberPlusHeader = false,
    stopAmberPlusLogoRedirect = false,
    showBackButton = false,
    showHamburger = true,
    showLangSelector = true,
    addAppPadding = true,
    appSafeAreaStyles,
    isReferralPage = false,
    isBlogHeader = false,
    isExamsHeader = false,
    isAmbassadorHeader = false,
    isScholarshipPage = false,
    isCareerPage = false,
    isAboutPage = false,
    isNewsHeader = false,
    isMediaMentionsHeader = false,
    isPodcastHeader = false,
    containerClassName,
    fromSearchPage = false,
    showHeaderAboveMd = false,
  } = props;

  const isContentHeader = isBlogHeader || isNewsHeader || isExamsHeader;

  const getLinkDestination = (
    blogHeader: boolean,
    examsHeader: boolean,
    ambassadorHeader: boolean,
    scholarshipPage: boolean,
  ) => {
    if (blogHeader) return "/blog";
    if (isNewsHeader) return "/news";
    if (examsHeader) return "/exams";
    if (ambassadorHeader) return "/ambassador";
    if (scholarshipPage) return "/scholarship";
    return "/";
  };

  const getLogoAltText = (
    blogHeader: boolean,
    examsHeader: boolean,
    ambassadorHeader: boolean,
    scholarshipPage: boolean,
  ) => {
    if (blogHeader) return "amber-blog-logo";
    if (isNewsHeader) return "amber-news-logo";
    if (examsHeader) return "amber-exams-logo";
    if (ambassadorHeader) return "ambassador-logo";
    if (scholarshipPage) return "scholarship-logo";
    return "amber-logo-dark";
  };
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const staticWordRef = useRef<HTMLSpanElement>(null);
  const animateContainerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { t } = useTranslationHook();
  const animationWords = [
    t("searchpage.components.header.city"),
    t("searchpage.components.header.university"),
    t("searchpage.components.header.property"),
  ];
  const isMiniProgram = useIsMiniProgram();
  const { isWhitelabel, whitelabelConfig, isDemoSubdomain, partner_uuid, isUniversityPartner } =
    useWhitelabel();
  const isApp = useIsApp();
  const isTabletPortrait = useIsTabletPortrait();
  const customWindowLocation = useCustomWindowLocation();
  const [showIpadNavSearch, setShowIpadNavSearch] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    const shouldAttach = isTabletPortrait && isWhitelabel && !isUniversityPartner;
    let listenerAttached = false;

    const handleScroll = () => {
      const heroRect = document.getElementById(HERO_SECTION_ID)?.getBoundingClientRect();
      const bottom = heroRect?.bottom;
      const isHeroOut =
        typeof bottom === "number" ? bottom < 0 : window.scrollY > IPAD_SCROLL_REVEAL_PX;
      setShowIpadNavSearch(isHeroOut);
    };

    if (shouldAttach) {
      handleScroll();
      window.addEventListener("scroll", handleScroll);
      listenerAttached = true;
    } else {
      setShowIpadNavSearch(false);
    }

    setHasInitialized(true);

    return () => {
      if (listenerAttached) window.removeEventListener("scroll", handleScroll);
    };
  }, [isTabletPortrait, isWhitelabel, isUniversityPartner]);

  // For whitelabel non-university: hide until initialized for no flash
  const isWhitelabelNonUniversity = isWhitelabel && !isUniversityPartner;
  const isTabletAndNotScrolledOut = isTabletPortrait && !showIpadNavSearch;
  const shouldHideForTabletPortrait =
    isWhitelabelNonUniversity && (!hasInitialized || isTabletAndNotScrolledOut);

  const shouldHideNavSearch =
    hideSearchBar ||
    isUniversityPartner ||
    shouldHideForTabletPortrait ||
    isAmbassadorHeader ||
    isScholarshipPage ||
    isCareerPage;

  const shouldShowNavSearch = !shouldHideNavSearch;
  const navigateToProfile = () => {
    if (isApp) {
      webAppBackHandler(history);
      return;
    }
    customWindowLocation({
      url: isWhitelabel
        ? convertToWhitelabelURL("/profile", isDemoSubdomain, partner_uuid)
        : "/profile",
    });
  };

  const handleKeyPressHome = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      navigateToProfile();
    }
  };

  const handleOnHamburger = () => {
    setShowSideMenu(true);
    handleEventEmitter({ action: EVENT_LABEL.MENU_CLICKED });
  };

  const handleEventEmitter = ({ action, label = "", ...rest }: any) => {
    gtagEventEmitter({
      action,
      category: COMPONENTS_CATEGORY.TOP_NAV,
      label: `${PAGE.SEARCH} ${label}`,
      ...rest,
    });
  };

  const handleWeChatSearchRedirection = () => {
    const userAuthToken = getCookie("user_auth_token") || "";
    // @ts-ignore
    if (window.wx && window.wx.miniProgram) {
      try {
        // Navigate to the mini program
        // @ts-ignore
        window.wx.miniProgram.navigateTo({
          url: `/pages/SearchFeature/SearchFeature?token=${encodeURIComponent(userAuthToken)}`,
        });
      } catch (error) {
        console.error("Failed to navigate to mini program:", error);
      }
    }
  };

  const handleSearchButtonClick = () => {
    if (isMiniProgram) {
      gtagEventEmitter({ action: "search_button_clicked", category: PAGE.SEARCH });
      handleWeChatSearchRedirection();
    } else {
      setShowSearchModal(true);
      gtagEventEmitter({ action: "search_button_clicked", category: PAGE.SEARCH });
    }
  };

  useEffect(() => {
    if (staticWordRef.current && animateContainerRef.current) {
      const staticWordWidth = staticWordRef.current.offsetWidth;
      animateContainerRef.current.style.setProperty(
        "--static-word-width",
        `${staticWordWidth + 15}px`,
      );
    }
  }, [t]);

  useEffect(() => {
    const animateWords = document.querySelectorAll(`.${styles.animateWords}`);
    animateWords.forEach((element: any) => {
      let index = 0;
      element.addEventListener("animationiteration", () => {
        element.innerHTML = animationWords[index % 3];
        index += 1;
      });
    });
  }, []);

  const handleBackButtonOnClick = () => {
    webAppBackHandler(history);
  };
  const appPadding = useAppPadding();

  let logoSrc = `${config.IMAGE_STATIC_ASSETS_COMPONENTS_PATH}/Header/assets/amber-logo-dark.svg`;
  if (isNewsHeader) {
    logoSrc = `${config.BLOG_ASSET_URL}/620e4101b2ce125835bff0bb/amber-news-logo.svg`;
  } else if (isBlogHeader) {
    logoSrc = config.IS_CHINA_APP
      ? `${config.CHINA_STATIC_ASSETS_URL}/620e4101b2ce125835bff0bb/amber-blog-logo.svg`
      : `${config.BLOG_ASSET_URL}/620e4101b2ce125835bff0bb/amber-blog-logo.svg`;
  } else if (config.IS_CHINA_APP) {
    logoSrc = `${config.IMAGE_STATIC_URL}/images/logo/amber-china-logo-dark.svg?auto=format&q=40`;
  }

  return (
    <>
      <AppSafeAreaSpacer enabled={addAppPadding} styles={appSafeAreaStyles} />
      <div
        className={formatClassNames(
          styles.container,
          addShadow && styles.addShadow,
          isScholarshipPage && styles.scholarshipHeader,
          isCareerPage && styles.careerHeader,
          isAboutPage && styles.aboutHeader,
          isMediaMentionsHeader && styles.mediaMentionsHeader,
          showHeaderAboveMd && styles.showAboveMd,
          containerClassName,
        )}
        {...(isApp &&
          addAppPadding && {
            style: { top: `${appPadding}px`, position: "sticky", zIndex: 10 },
          })}
      >
        <>
          {showBackButton && (
            <div className={styles.backButton} onClick={handleBackButtonOnClick} role="none">
              <Chevron rotate="90" height="16" width="16" />
            </div>
          )}
          {isMiniProgram || showBackButton ? null : (
            <>
              {!isProfileHeader && (
                <div className={styles.leftSection}>
                  {isWhitelabel && !whitelabelConfig?.logo && (
                    <div className={styles.logoPlaceholder} />
                  )}
                  {isWhitelabel && whitelabelConfig?.logo && (
                    <CustomLink
                      to={
                        whitelabelConfig?.redirect_url
                          ? formatValidOuterLink(whitelabelConfig?.redirect_url)
                          : ""
                      }
                      target="_blank"
                      rel="noopener"
                      redirect={Boolean(whitelabelConfig?.redirect_url)}
                      stopLink={Boolean(!whitelabelConfig?.redirect_url)}
                      onClick={() =>
                        gtagEventEmitter({ action: "clicked_whitelabel_logo", category: "" })
                      }
                    >
                      <LazyImage
                        src={getImagePath(whitelabelConfig?.logo || "")}
                        isNotLazy
                        height={20}
                        width="auto"
                        alt={whitelabelConfig?.domain?.title || ""}
                        isWhitelabelIframeImage
                      />
                    </CustomLink>
                  )}
                  {!isWhitelabel && !isAmberPlusHeader && (
                    <CustomLink
                      to={getLinkDestination(
                        isBlogHeader,
                        isExamsHeader,
                        isAmbassadorHeader,
                        isScholarshipPage,
                      )}
                      aria-label="home"
                      data-testid={`${dataTestId}-amber-logo`}
                      redirect={isContentHeader}
                      className={styles.logoLink}
                      onClick={(e: React.MouseEvent) => {
                        if (isPodcastHeader) {
                          e.preventDefault();
                        } else if (isAmbassadorHeader || isScholarshipPage) {
                          e.preventDefault();
                          window.location.reload();
                        }
                      }}
                    >
                      {(() => {
                        if (isAmbassadorHeader) {
                          return (
                            <LazyImage
                              src={`${AMBASSADOR_PAGE_STATIC_URL}/ambassador_dark_logo.png`}
                              isNotLazy={!isMiniProgram}
                              height={20}
                              width={281.3}
                              alt="ambassador-logo"
                              dataTestId="Ambassador-logo"
                              onClick={() =>
                                gtagEventEmitter({
                                  action: "clicked_ambassador_logo",
                                  category: "",
                                })
                              }
                            />
                          );
                        }
                        if (isScholarshipPage) {
                          return (
                            <LazyImage
                              src={`${SCHOLARSHIP_PAGE_STATIC_URL}/scholarship_logo_dark.svg`}
                              isNotLazy={!isMiniProgram}
                              height={20}
                              width={281.3}
                              alt="scholarship-logo"
                              dataTestId="Scholarship-logo"
                              onClick={() =>
                                gtagEventEmitter({
                                  action: "clicked_scholarship_logo",
                                  category: "",
                                })
                              }
                            />
                          );
                        }
                        if (isPodcastHeader) {
                          return <BlueprintLogoSmall />;
                        }
                        return (
                          <LazyImage
                            src={logoSrc}
                            isNotLazy={!isMiniProgram}
                            height={20}
                            width={isContentHeader ? 110 : 83}
                            alt={getLogoAltText(
                              isBlogHeader,
                              isExamsHeader,
                              isAmbassadorHeader,
                              isScholarshipPage,
                            )}
                            dataTestId="Amber-logo"
                            onClick={() =>
                              gtagEventEmitter({ action: "clicked_amber", category: "" })
                            }
                          />
                        );
                      })()}
                    </CustomLink>
                  )}
                  {!isWhitelabel && isAmberPlusHeader && (
                    <CustomLink
                      to="/plus"
                      redirect
                      stopLink={stopAmberPlusLogoRedirect}
                      aria-label="amber-plus-home"
                      className={styles.logoLink}
                    >
                      <LazyImage
                        src={`${config.IMAGE_STATIC_URL}/images/amber-plus-logo.svg?auto=format`}
                        isNotLazy={!isMiniProgram}
                        height={32}
                        width={112}
                        alt="amber-plus-logo"
                        onClick={() =>
                          gtagEventEmitter({ action: "clicked_amber_plus", category: "" })
                        }
                      />
                    </CustomLink>
                  )}
                </div>
              )}
            </>
          )}
        </>

        {!isProfileHeader && (
          <div
            className={formatClassNames(
              styles.rightSection,
              shouldHideNavSearch && styles.justifyEnd,
            )}
          >
            {shouldShowNavSearch && (
              <div
                className={styles.searchWrap}
                role="button"
                tabIndex={0}
                onClick={() => {
                  handleSearchButtonClick();
                }}
                data-testid="mobile-searchPage-searchBar-header"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleSearchButtonClick();
                  }
                }}
              >
                <div className={styles.animateContainer} ref={animateContainerRef}>
                  {isBlogHeader && (
                    <span className={styles.staticWord}>{t("blog.header.searchblogs")}</span>
                  )}
                  {isNewsHeader && (
                    <span className={styles.staticWord}>{t("news.header.searchnews")}</span>
                  )}
                  {isExamsHeader && (
                    <span className={styles.staticWord}>{t("exams.header.searchexams")}</span>
                  )}
                  {!isContentHeader && (
                    <>
                      <span className={styles.staticWord} ref={staticWordRef}>
                        {t("searchpage.components.header.searchtitle")}
                      </span>
                      <span className={styles.animateWords} />
                    </>
                  )}
                </div>
                <i className={styles.search}>
                  {showSearchModal ? (
                    <LoaderIcon fill="#ffffff" size={16} />
                  ) : (
                    <SearchIcon size="14" />
                  )}
                </i>
              </div>
            )}
            {!showBackButton && showHamburger && (
              <i
                className={styles.hamburger}
                role="button"
                tabIndex={0}
                onClick={handleOnHamburger}
                data-testid="mobile-hamburger-menu"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleOnHamburger();
                  }
                }}
                aria-label="menu"
              >
                <HamburgurIcon />
              </i>
            )}
          </div>
        )}
        {isProfileHeader && (
          <div className={styles.profileHeaderCss}>
            <div className={styles.leftSectionHeading}>
              {isSubProfileHeader && (
                <span
                  className={styles.chevronSpanForSubHeaders}
                  onClick={navigateToProfile}
                  onKeyPress={handleKeyPressHome}
                  tabIndex={0}
                  role="button"
                  aria-label="Back to profile"
                >
                  <Chevron rotate="90" height="12" width="12" />
                </span>
              )}
              <h3 className={formatClassNames(isWhitelabel ? "wl-title-font" : "")}>
                {t(profileHeaderTitle || "")}
              </h3>
            </div>
            {showHamburger && (
              <div>
                <i
                  className={styles.hamburger}
                  role="button"
                  onClick={handleOnHamburger}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handleOnHamburger();
                    }
                  }}
                  aria-label="menu"
                >
                  <HamburgurIcon />
                </i>
              </div>
            )}
          </div>
        )}
      </div>
      {showSideMenu && (
        <Portal rootId="forPortal">
          <Modal
            hideModal={() => setShowSideMenu(false)}
            size="fullScreen"
            animation="bottomToTop"
            isHistoryPush
            type="sideMenu"
          >
            <Suspense fallback={<FullPageLoader />}>
              <DropDownMenu
                showAppBanner={false}
                handleEventEmitter={handleEventEmitter}
                isReferralPage={isReferralPage}
                isAmberPlusHeader={isAmberPlusHeader}
                isBlogHeader={isBlogHeader}
                isNewsHeader={isNewsHeader}
                isExamsHeader={isExamsHeader}
                showLangSelector={showLangSelector && !isNewsHeader}
              />
            </Suspense>
          </Modal>
        </Portal>
      )}
      {showSearchModal && (
        <Portal rootId="forPortal">
          <Modal
            hideModal={() => setShowSearchModal(false)}
            size="fullScreen"
            animation="bottomToTop"
            hideCloseButton
            isHistoryPush
            type="searchModal"
          >
            <Suspense fallback={<FullPageLoader />}>
              <SearchModalMobile
                setShowSearchModal={setShowSearchModal}
                isBlogSearch={isBlogHeader}
                isNewsSearch={isNewsHeader}
                isExamsSearch={isExamsHeader}
                fromSearchPage={fromSearchPage}
              />
            </Suspense>
          </Modal>
        </Portal>
      )}
    </>
  );
};

export default Header;
